const normalizeText = (text) => String(text || "")
  .replace(/[๐-๙]/g, (digit) => String(digit.charCodeAt(0) - 0x0e50))
  .replace(/[–—−]/g, "-")
  .replace(/₂/g, "2")
  .replace(/\s+/g, " ");

const validRange = (minimum, maximum) => {
  if (minimum === undefined || maximum === undefined ||
      String(minimum).trim() === "" || String(maximum).trim() === "") return null;
  const min = Number(minimum);
  const max = Number(maximum);
  return Number.isFinite(min) && Number.isFinite(max) && min > 0 && min <= max
    ? { minPressure: min, maxPressure: max }
    : null;
};

// Require an explicit pressure label or unit. Multiple different readings are
// ambiguous (for example an old setting crossed out beside a new setting).
export const extractAnnualMaskSignedPressure = (rawText) => {
  const text = normalizeText(rawText);
  const number = "(\\d+(?:\\.\\d+)?)";
  const range = `${number}\\s*(?:-|to|ถึง)\\s*${number}`;
  const unit = "(?:cm\\s*h\\s*2\\s*[o0]|เซนติเมตรน้ำ)";
  const patterns = [
    new RegExp(`${range}\\s*${unit}`, "gi"),
    new RegExp(`(?:pressure|แรงดัน(?:ลม)?|ความดัน(?:ลม)?)\\s*[:=]?\\s*${range}(?![\\d.])`, "gi"),
  ];
  const candidates = [];
  let invalid = false;
  patterns.forEach((pattern) => {
    let match;
    while ((match = pattern.exec(text))) {
      const value = validRange(match[1], match[2]);
      if (value) candidates.push(value);
      else invalid = true;
    }
  });

  const minMatches = Array.from(text.matchAll(/(?:min(?:imum)?\s*pressure|แรงดันต่ำสุด)\s*[:=]?\s*(\d+(?:\.\d+)?)/gi));
  const maxMatches = Array.from(text.matchAll(/(?:max(?:imum)?\s*pressure|แรงดันสูงสุด)\s*[:=]?\s*(\d+(?:\.\d+)?)/gi));
  if (minMatches.length || maxMatches.length) {
    if (minMatches.length !== 1 || maxMatches.length !== 1) return null;
    const value = validRange(minMatches[0][1], maxMatches[0][1]);
    if (!value) return null;
    candidates.push(value);
  }

  // Fixed pressure is comparable to a range with identical endpoints.
  const fixedPattern = new RegExp(`(?:pressure|แรงดัน(?:ลม)?|ความดัน(?:ลม)?)\\s*[:=]?\\s*${number}\\s*${unit}`, "gi");
  let fixed;
  while (!minMatches.length && !maxMatches.length && (fixed = fixedPattern.exec(text))) {
    const value = validRange(fixed[1], fixed[1]);
    if (value) candidates.push(value);
    else invalid = true;
  }

  const unique = Array.from(new Map(candidates.map((value) => [
    `${value.minPressure}:${value.maxPressure}`, value,
  ])).values());
  return !invalid && unique.length === 1 ? unique[0] : null;
};

export const compareAnnualMaskSignedPressure = (text, certificateData = {}) => {
  const signed = extractAnnualMaskSignedPressure(text);
  if (!signed) return { status: "unreadable" };
  let original = validRange(certificateData.minPressure, certificateData.maxPressure);
  if (!original && !certificateData.minPressure && !certificateData.maxPressure) {
    original = extractAnnualMaskSignedPressure(`Pressure: ${certificateData.pressure || ""}`);
  }
  if (!original) return { status: "missing_original", signed };
  return {
    status: original.minPressure !== signed.minPressure || original.maxPressure !== signed.maxPressure
      ? "changed" : "unchanged",
    original,
    signed,
  };
};
